import { useEffect, useState, useCallback } from "react";
import { AdminApi, type AdminOrg, type AdminOrgDetail } from "@/api/adminApi";
import { RestClient } from "@/api/restClient";

const apiUrl = import.meta.env.VITE_API_URL || "";
const client = new RestClient({ apiUrl });
const adminApi = new AdminApi(client);

/** Per-organization "platform knowledge" switch (decision C). Off = the org's
 *  agents and Documents page see no platform documents. Shows the stored
 *  value only — a failed change leaves it as it was and says why. */
function PlatformKnowledgeToggle({ companyId }: { companyId: string }) {
  const [allow, setAllow] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAllow(null);
    setError(null);
    adminApi.getPlatformKnowledge(companyId).then(
      (r) => { if (!cancelled) setAllow(r.allow_platform_knowledge); },
      (e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); },
    );
    return () => { cancelled = true; };
  }, [companyId]);

  const onChange = async (next: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi.setPlatformKnowledge(companyId, next);
      setAllow(r.allow_platform_knowledge);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: 16, padding: 12, background: "var(--surface-bg, #f9f9f9)", borderRadius: 6 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
        <input
          type="checkbox"
          checked={allow ?? false}
          disabled={allow === null || busy}
          onChange={(e) => onChange(e.target.checked)}
        />
        Platform knowledge
      </label>
      <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-secondary, #666)" }}>
        Shared platform documents of the organization&apos;s industry, in its agents and
        Documents page.
      </div>
      {error && <div style={{ marginTop: 4, fontSize: 11, color: "red" }}>{error}</div>}
    </div>
  );
}

export function AdminOrgs() {
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState<AdminOrgDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listOrgs({ page, limit: 25, search: search || undefined });
      setOrgs(res.items);
      setTotal(res.total);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const openDetail = async (companyId: string) => {
    setDetailLoading(true);
    try {
      const detail = await adminApi.getOrgDetail(companyId);
      setSelectedOrg(detail);
    } catch {
      // error
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSponsor = async (companyId: string) => {
    const reason = prompt("Sponsorship reason:");
    if (!reason) return;
    await adminApi.grantSponsorship(companyId, reason);
    loadOrgs();
    if (selectedOrg?.id === companyId) openDetail(companyId);
  };

  const handleRevokeSponsor = async (companyId: string) => {
    if (!confirm("Revoke sponsorship?")) return;
    await adminApi.revokeSponsorship(companyId);
    loadOrgs();
    if (selectedOrg?.id === companyId) openDetail(companyId);
  };

  const handleSuspend = async (companyId: string) => {
    const reason = prompt("Suspension reason:");
    if (reason === null) return;
    await adminApi.suspendOrg(companyId, reason);
    loadOrgs();
    if (selectedOrg?.id === companyId) openDetail(companyId);
  };

  return (
    <div style={{ display: "flex", gap: 24 }}>
      {/* List */}
      <div style={{ flex: 1 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Organizations</h1>

        <input
          type="text"
          placeholder="Search by org name..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--border-color, #e0e0e0)", borderRadius: 6, fontSize: 14, marginBottom: 16 }}
        />

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border-color, #e0e0e0)" }}>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Name</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Members</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Plan</th>
              <th style={{ textAlign: "left", padding: "8px 4px" }}>Status</th>
              <th style={{ textAlign: "right", padding: "8px 4px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && !orgs.length ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: "center" }}>Loading...</td></tr>
            ) : orgs.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: "center" }}>No organizations found</td></tr>
            ) : (
              orgs.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => openDetail(o.id)}
                  style={{
                    borderBottom: "1px solid var(--border-color, #f0f0f0)",
                    cursor: "pointer",
                    background: selectedOrg?.id === o.id ? "var(--primary-bg, #eff6ff)" : undefined,
                  }}
                >
                  <td style={{ padding: "8px 4px", fontWeight: 500 }}>{o.name}</td>
                  <td style={{ padding: "8px 4px" }}>{o.member_count}</td>
                  <td style={{ padding: "8px 4px" }}>{o.plan_id ?? "—"}</td>
                  <td style={{ padding: "8px 4px" }}>
                    {o.suspended ? (
                      <span style={{ color: "red", fontWeight: 500 }}>Suspended</span>
                    ) : o.is_sponsored ? (
                      <span style={{ color: "#7c3aed", fontWeight: 500 }}>Sponsored</span>
                    ) : (
                      <span style={{ color: "green" }}>{o.subscription_status || "No plan"}</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 4px", textAlign: "right" }}>
                    {!o.is_sponsored && (
                      <button onClick={(e) => { e.stopPropagation(); handleSponsor(o.id); }}
                        style={{ fontSize: 11, padding: "2px 6px", cursor: "pointer", marginRight: 4 }}>
                        Sponsor
                      </button>
                    )}
                    {o.is_sponsored && (
                      <button onClick={(e) => { e.stopPropagation(); handleRevokeSponsor(o.id); }}
                        style={{ fontSize: 11, padding: "2px 6px", cursor: "pointer", color: "orange" }}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, fontSize: 13 }}>
          <span style={{ color: "var(--text-secondary, #666)" }}>{total} orgs total</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</button>
            <span>Page {page}</span>
            <button disabled={orgs.length < 25} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedOrg && (
        <div style={{ width: 380, borderLeft: "1px solid var(--border-color, #e0e0e0)", paddingLeft: 24, fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{selectedOrg.name}</h2>
            <button onClick={() => setSelectedOrg(null)} style={{ cursor: "pointer", fontSize: 18, border: "none", background: "none" }}>×</button>
          </div>

          {detailLoading ? <p>Loading...</p> : (
            <>
              {/* Subscription */}
              {selectedOrg.subscription && (
                <div style={{ marginBottom: 16, padding: 12, background: "var(--surface-bg, #f9f9f9)", borderRadius: 6 }}>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>Subscription</div>
                  <div>Status: <strong>{selectedOrg.subscription.status}</strong></div>
                  <div>Seats: {selectedOrg.subscription.seats_used}/{selectedOrg.subscription.seat_limit}
                    {(selectedOrg.subscription.sponsored_seats > 0) && ` (+${selectedOrg.subscription.sponsored_seats} sponsored)`}
                  </div>
                  {selectedOrg.subscription.sponsored_reason && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "#7c3aed" }}>
                      Sponsored: {selectedOrg.subscription.sponsored_reason}
                    </div>
                  )}
                </div>
              )}

              <PlatformKnowledgeToggle companyId={selectedOrg.id} />

              {/* Members */}
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                  Members ({selectedOrg.member_count})
                </h3>
                <div style={{ maxHeight: 200, overflow: "auto" }}>
                  {selectedOrg.members.map((m) => (
                    <div key={m.user_id} style={{ padding: "4px 0", borderBottom: "1px solid var(--border-color, #f0f0f0)" }}>
                      <div>{m.email}</div>
                      <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>
                        {m.suspended ? "Suspended" : "Active"}
                        {m.last_active_at && ` · ${new Date(m.last_active_at).toLocaleDateString()}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                <div style={{ padding: 8, background: "var(--surface-bg, #f9f9f9)", borderRadius: 4, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>Plugins</div>
                  <div style={{ fontWeight: 600 }}>{selectedOrg.plugin_count}</div>
                </div>
                <div style={{ padding: 8, background: "var(--surface-bg, #f9f9f9)", borderRadius: 4, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>Docs</div>
                  <div style={{ fontWeight: 600 }}>{selectedOrg.document_count}</div>
                </div>
                <div style={{ padding: 8, background: "var(--surface-bg, #f9f9f9)", borderRadius: 4, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>Schedules</div>
                  <div style={{ fontWeight: 600 }}>{selectedOrg.schedule_count}</div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => handleSuspend(selectedOrg.id)}
                  style={{ fontSize: 12, padding: "4px 12px", cursor: "pointer", color: "red", border: "1px solid red", borderRadius: 4, background: "transparent" }}>
                  Suspend Org
                </button>
                <button onClick={() => handleSponsor(selectedOrg.id)}
                  style={{ fontSize: 12, padding: "4px 12px", cursor: "pointer", border: "1px solid #7c3aed", color: "#7c3aed", borderRadius: 4, background: "transparent" }}>
                  Grant Sponsor
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
